import React, { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { toast } from "sonner";
import { Mail, RefreshCw, CheckCircle2 } from "lucide-react";

export default function VerifyEmailBanner() {
  const { user } = useAuth();
  const [sending, setSending] = useState(false);

  if (!user) return null;
  if (user.email_verified) return null;

  const resend = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/auth/resend-verification");
      if (data?.already_verified) {
        toast.success("Your email is already verified. Refresh to unlock features.");
      } else if (data?.sent === false) {
        toast.error(
          "We couldn't deliver the email right now. Please email support@qatestology.com and we'll verify you manually.",
          { duration: 8000 }
        );
      } else {
        toast.success(`Verification email sent to ${user.email}. Please also check your spam folder.`, { duration: 6000 });
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to send email");
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="p-5 mb-6 border-amber-200 bg-amber-50" data-testid="verify-email-banner">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="h-10 w-10 rounded-md bg-amber-100 grid place-items-center text-amber-700 flex-shrink-0">
          <Mail className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <h3 className="font-display font-bold text-zinc-950">Verify your email to unlock more features</h3>
          <p className="text-sm text-zinc-700 mt-1">
            We sent a verification link to <span className="font-mono font-semibold">{user.email}</span>.
            You can update your profile now, but{" "}
            <span className="font-semibold">
              {user.role === "employer"
                ? "posting jobs, inviting candidates and using AI candidate match"
                : "uploading a resume, applying to jobs and running AI analysis"}
            </span>{" "}
            stay locked until you verify.
          </p>
        </div>
        <Button
          onClick={resend}
          disabled={sending}
          data-testid="resend-verification-btn"
          className="bg-amber-600 hover:bg-amber-700 text-white"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${sending ? "animate-spin" : ""}`} />
          {sending ? "Sending..." : "Resend email"}
        </Button>
      </div>
    </Card>
  );
}

export function VerifiedBadge() {
  return (
    <span className="inline-flex items-center gap-1 text-xs text-emerald-700 tag-mono">
      <CheckCircle2 className="h-3 w-3" /> verified
    </span>
  );
}
